// ============================================================================
// LE TRANSFERT WALLET, VU DE LA CONSOLE.
//
// POURQUOI CET ÉCRAN (question utilisateur 2026-08-31 : « comment on sait s'il fonctionne ? ») : un
// worker de fond qui travaille sans laisser de trace est indistinguable d'un worker en panne. On
// montre donc son TRAVAIL — la file telle qu'elle est, magasin par magasin — et non un voyant.
//
// ⚠️ « ZÉRO EN ATTENTE » N'EST PAS « TOUT VA BIEN ». Ça peut aussi vouloir dire que le lanceur est
// arrêté, ou que personne n'a vendu. C'est pourquoi l'écran montre TOUJOURS trois choses ensemble :
// l'état du lanceur, l'âge du plus ancien en attente, et l'état du jeton par magasin. Aucune ne suffit.
//
// ⚠️ CE QUI N'EST PAS SU EST DIT : un magasin dont la file est illisible apparaît nommé, jamais
// silencieusement absent — absent, il compterait comme sain (§23.0).
// ============================================================================
import { useEffect, useId, useState, type CSSProperties } from 'react';
import { alert, confirm, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { api, type WalletState } from '../api';
import { formPanelRef, Pill } from '../ui';

/** Durée écoulée, en mots — « depuis 4 min » se lit mieux qu'un horodatage à soustraire de tête. */
function depuis(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86_400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86_400)} j`;
}

const JETON = {
  ok: { tone: 'ok', txt: 'jeton posé' },
  absent: { tone: 'wa', txt: 'aucun jeton' },
  illisible: { tone: 'da', txt: 'jeton illisible' },
} as const;

export function WalletPanel() {
  /** Le titre de la liste des magasins visés, que la liste désigne. */
  const targetsId = useId();
  const [state, setState] = useState<WalletState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [jetonPour, setJetonPour] = useState<string | null>(null);
  const [token, setJeton] = useState('');
  const [targets, setCibles] = useState<string[]>([]);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Le focus va au jeton par ce socle, jamais par `autoFocus` : posé avant lui, il serait retenu comme déclencheur.
  const { panelRef: tokenPanel } = useOverlayPanel({ open: jetonPour !== null, onClose: () => setJetonPour(null) });
  const tokenTitle = useId();

  const load = () => api.boWallet()
    .then((r) => { setState(r); setErr(null); })
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const relancer = async (gln: string) => {
    if (!(await confirm({
      title: `Relancer le transfert du magasin ${gln} ?`,
      message: 'Les envois abandonnés repartiront. À ne faire qu’après avoir corrigé la cause — sinon ils échoueront à nouveau.',
      confirmLabel: 'Relancer',
    }))) return;
    setBusy(gln);
    try {
      const r = await api.retryBoWallet(gln);
      showToast(`${r.retried} envoi${r.retried > 1 ? 's' : ''} remis en file.`);
      await load();
    } catch (e) { void alert({ title: 'Relance refusée', message: (e as Error).message }); }
    finally { setBusy(null); }
  };

  const poserJeton = async () => {
    if (!jetonPour || !token.trim() || targets.length === 0) return;
    setBusy(jetonPour);
    try {
      const r = await api.setBoWalletToken(targets, token.trim());
      showToast(r.detail);
      setJetonPour(null); setJeton(''); setCibles([]);
      await load();
    } catch (e) { void alert({ title: 'Jeton refusé', message: (e as Error).message }); }
    finally { setBusy(null); }
  };

  /** Ouvre la pose en pré-cochant le magasin cliqué — les autres restent à cocher sciemment. */
  const ouvrirPose = (gln: string) => { setJetonPour(gln); setJeton(''); setCibles([gln]); };

  // ⚠️ L'erreur PASSE AVANT le chargement — sinon un back-office HS laisse la carte sur « Chargement… »
  //    pour toujours, l'erreur posée mais jamais rendue.
  if (err) {
    return (
      <section className="block">
        <div className="blockhead"><h3>Transfert wallet</h3></div>
        <div className="err">État du transfert indisponible : {err}</div>
      </section>
    );
  }
  if (!state) {
    return (
      <section className="block">
        <div className="blockhead"><h3>Transfert wallet</h3></div>
        <div className="muted">Chargement de l’état du transfert…</div>
      </section>
    );
  }

  const r = state.runner;
  const jetonDe = new Map(state.tokens.map((j) => [j.gln, j.token]));
  const lbl: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', marginBottom: 4 };
  const val: CSSProperties = { fontSize: 'var(--t-control)', fontWeight: 700 };

  return (
    <section className="block">
      <div className="blockhead">
        <h3>Transfert wallet</h3>
        {/* ⚠️ L'ÉTAT DU LANCEUR EN PREMIER : une file vide sur un lanceur arrêté n'est pas « à jour ». */}
        {r.started
          ? <Pill tone={r.lastRunAt ? 'ok' : 'wa'}>{r.lastRunAt ? `dernier passage il y a ${depuis(r.lastRunAt)}` : 'démarré, aucun passage encore'}</Pill>
          : <Pill tone="da">à l’arrêt — rien ne part</Pill>}
      </div>

      <p className="muted" style={{ fontSize: 'var(--t-body)', marginTop: 0, lineHeight: 1.55 }}>
        Le back-office publie le solde de fidélité sur les pass, <b>hors du temps de la vente</b>. Une
        ligne par client (le solde est une valeur absolue), donc plusieurs achats dans la journée
        produisent un envoi par cycle, portant le solde courant.
      </p>

      <div className="dcard" style={{ padding: '13px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px 18px', marginBottom: 12 }}>
        <div>
          <div style={lbl}>Cadence</div>
          <div style={val}>{r.cadenceSec} s</div>
          {/* D'où vient la valeur : « défaut » n'est pas une panne, mais ça se dit. */}
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{r.cadenceSource === 'réglage' ? 'réglée ci-dessus' : 'valeur par défaut'}</div>
        </div>
        <div>
          <div style={lbl}>En attente</div>
          <div style={val}>{state.total.pending}</div>
        </div>
        <div>
          <div style={lbl}>Abandons</div>
          <div style={{ ...val, color: state.total.dead ? 'var(--da-fg)' : undefined }}>{state.total.dead}</div>
        </div>
        <div>
          <div style={lbl}>Cycles / envois</div>
          <div style={val}>{r.runningTotals.cycles} / {r.runningTotals.sent}</div>
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>depuis le démarrage du service</div>
        </div>
      </div>

      {r.lastError && (
        <div className="err" style={{ marginBottom: 12 }}>
          Le dernier cycle n’a pas pu être tenté : {r.lastError}
        </div>
      )}

      {state.stores.length === 0 && state.unreadable.length === 0 ? (
        <div className="muted">Aucun magasin servi par ce back-office.</div>
      ) : (
        <div className="dcard" style={{ overflow: 'hidden' }}>
          {state.stores.map((m) => {
            const j = jetonDe.get(m.gln) ?? 'absent';
            const t = JETON[j];
            return (
              <div key={m.gln} style={{ padding: '12px 16px', borderTop: '1px solid var(--ink200)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span className="mono" style={{ fontWeight: 600 }}>{m.gln}</span>
                  <Pill tone={t.tone}>{t.txt}</Pill>
                  <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>
                    {m.pending} en attente
                    {/* ⚠️ L'ÂGE, pas seulement le compte : c'est lui qui distingue « à jour » de « bloqué ». */}
                    {m.oldestAt != null && ` · le plus ancien depuis ${depuis(m.oldestAt)}`}
                  </span>
                  <span style={{ flex: 1 }} />
                  <button className="btn-ghost" style={{ height: 32, padding: '0 12px', borderRadius: 'var(--r-sm)', fontWeight: 600 }}
                    onClick={() => ouvrirPose(m.gln)}>
                    {j === 'ok' ? 'Remplacer le jeton' : 'Poser un jeton'}
                  </button>
                  {m.dead.length > 0 && (
                    <button className="btn btn-cta sm" style={{ width: 'auto' }} disabled={busy === m.gln}
                      onClick={() => void relancer(m.gln)}>
                      {busy === m.gln ? '…' : `Relancer ${m.dead.length}`}
                    </button>
                  )}
                </div>

                {/* Les abandons AVEC leur motif : un nombre ne dit pas quoi corriger. */}
                {m.dead.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
                    {m.dead.slice(0, 5).map((d) => (
                      <div key={d.clientId} style={{ color: 'var(--da-fg)' }}>
                        <span className="mono">{d.clientId}</span> — {d.error} <span style={{ color: 'var(--ink500)' }}>({d.attempts} essais)</span>
                      </div>
                    ))}
                    {m.dead.length > 5 && (
                      <div style={{ color: 'var(--ink500)' }}>… et {m.dead.length - 5} autre(s).</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* ⚠️ NOMMÉS, jamais absents : un magasin illisible qui disparaîtrait compterait comme sain. */}
          {state.unreadable.map((x) => (
            <div key={x.gln} style={{ padding: '12px 16px', borderTop: '1px solid var(--ink200)' }}>
              <span className="mono" style={{ fontWeight: 600 }}>{x.gln}</span>{' '}
              <Pill tone="da">file illisible</Pill>{' '}
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--da-fg)' }}>{x.error}</span>
            </div>
          ))}
        </div>
      )}

      {jetonPour && (
        <div className="modalwrap" onClick={() => setJetonPour(null)}>
          <form className="modal" ref={formPanelRef(tokenPanel)} role="dialog" aria-modal="true" aria-labelledby={tokenTitle}
            onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); void poserJeton(); }}>
            <h3 id={tokenTitle}>Jeton Eldowallet — magasin {jetonPour}</h3>
            <label htmlFor="wtok">Jeton opaque du portefeuille</label>
            <input id="wtok" className="mono" type="password" autoComplete="new-password"
              value={token} onChange={(e) => setJeton(e.target.value)} required />
            {/* ⚠️ LE COMPTE ELDOWALLET APPARTIENT AU CLIENT, et c'est SON choix d'en utiliser un seul
                pour ses X magasins (décision 2026-08-31). Sans ce choix multiple, faire tourner un
                jeton partagé demanderait de le recoller à la main autant de fois qu'il y a de
                magasins. Chaque magasin garde sa propre ligne : l'un peut basculer sur un autre
                compte sans toucher aux autres. */}
            <span id={targetsId} className="field-title" style={{ marginTop: 12 }}>Appliquer à</span>
            <div role="group" aria-labelledby={targetsId} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 190, overflowY: 'auto', marginTop: 4 }}>
              {state.stores.map((m) => (
                <label key={m.gln} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-note)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={targets.includes(m.gln)}
                    onChange={(e) => setCibles((c) => (e.target.checked ? [...c, m.gln] : c.filter((g) => g !== m.gln)))} />
                  <span className="mono">{m.gln}</span>
                  <span style={{ color: 'var(--ink500)' }}>
                    {(jetonDe.get(m.gln) ?? 'absent') === 'ok' ? 'jeton déjà posé — il sera remplacé' : 'aucun jeton'}
                  </span>
                </label>
              ))}
            </div>

            <div className="opt" style={{ marginTop: 8, lineHeight: 1.5 }}>
              {/* Ce que l'opérateur doit savoir AVANT de valider, pas après. */}
              Le back-office <b>éprouve le jeton une fois</b> chez Eldowallet avant de l’enregistrer :
              refusé, rien n’est conservé et le motif du fournisseur s’affiche. Un magasin inconnu fait
              refuser <b>tout le lot</b> — jamais une pose à moitié. Le jeton ne redescend jamais
              ensuite : il ne peut être que remplacé.
            </div>
            <div className="modalfoot">
              <button type="button" className="ghost btn-ghost" onClick={() => setJetonPour(null)}>Annuler</button>
              <button type="submit" className="btn btn-cta" disabled={busy === jetonPour || !token.trim() || targets.length === 0}>
                {busy === jetonPour ? 'Vérification…' : `Éprouver et enregistrer (${targets.length})`}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
